import { toastError } from '@onestep/coreui/Toast';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import type { ParameterConfig } from '@/types/measurements';

import { isPoundInchType } from '../../components/MeasurementsAction/MeasurementHelper';
import useFeatureMap from '../../hooks/useFeatureMap';
import useMixpanel from '../../hooks/useMixpanel';
import useStores from '../../hooks/useStores';
import { transformNormsResult } from './normsServiceHelper';
import type { INormV2 } from './types';

const withholdFallRiskThresholds = <T extends { fraNorms: INormV2[] }>(norms: T): T => ({
  ...norms,
  fraNorms: [],
});

export default function useNormsServiceV2(patientUuid: string) {
  const { trackFail } = useMixpanel();
  const { measurementsStore, appStore } = useStores();
  const isImperial = isPoundInchType(measurementsStore.unitsMapping);
  const { disableFallRiskNorms } = useFeatureMap();

  const { data, isLoading, isFetching } = useQuery<{
    metadata: ParameterConfig[];
    fraNorms: INormV2[];
    specificNorms: INormV2[];
  }>({
    queryKey: ['camel', 'config', 'norms', patientUuid],
    enabled: !!patientUuid,
    select: (data) => {
      const transformed = transformNormsResult(data, isImperial);
      return disableFallRiskNorms ? withholdFallRiskThresholds(transformed) : transformed;
    },
    onError: (error) => {
      toastError({ content: 'Failed to fetch norms data', toastId: 'FailedFetchingNorms' });
      trackFail('fetch_norms', { error });
    },
  });

  useEffect(() => {
    if (data?.metadata) {
      // Transform 'units' inside 'appStore.gaitParams'
      appStore.updateGaitParams(data.metadata, isImperial);
    }
  }, [data?.metadata]);

  const getNormByParameter = useCallback(
    (parameter: string): INormV2 | undefined => {
      return data?.specificNorms.find((norm) => norm.key === parameter);
    },
    [data?.specificNorms],
  );
  return {
    parameterConfigs: data?.metadata ?? [],
    fraNorms: data?.fraNorms ?? [],
    specificNorms: data?.specificNorms ?? [],
    isLoading,
    isFetching,
    getNormByParameter,
  };
}
